import { toRefs, unref } from 'vue'
import {
  cancelKioskServicePrompt,
  isKioskServicePromptOpen
} from '@/composables/useKioskServiceUnlock'
import usePlayer from '@/composables/usePlayer'
import { consumeWifiSetupBack, isWifiSetupSheetOpen } from '@/composables/useWifiProvisioning'
import { isAndroidPlayStoreDistribution } from '@/helpers/androidDistribution'
import { resolveAndroidBackAction } from '@/helpers/resolveAndroidBackAction'
import { createStatusOverlayGesture } from '@/helpers/statusOverlayTripleTap'
import { getPlatform, getPlatformInstance } from '@/platform'

const { state, setShowDebugModal, toggleStatusOverlayFromLocalGesture } = usePlayer()
const { mouseMoving, isFullScreen, showDebugModal } = toRefs(state)

let mouseInterval: ReturnType<typeof setInterval> | null = null

const statusOverlayGesture = createStatusOverlayGesture({
  onTripleTap: () => {
    toggleStatusOverlayFromLocalGesture()
  },
  isSuppressed: isKioskServicePromptOpen,
  isOverlayOpen: () => unref(showDebugModal)
})

/**
 * Capture-phase so taps on widget overlays (stopPropagation) still count.
 * Remote OK is a key, not a click, on Android TV boxes. Ignore while the kiosk PIN is open.
 */
function statusOverlayTripleTapClickHandler(_event: MouseEvent) {
  statusOverlayGesture.onClick()
}

function statusOverlayTripleTapKeyHandler(event: KeyboardEvent) {
  statusOverlayGesture.onKeyDown(event)
}

/**
 * Android Back: close PIN, then the Wi-Fi sheet, then the status panel.
 * Direct/kiosk (Home) builds do not finish the activity.
 * Play Store builds keep normal Back-exits after those dismissals.
 */
function backButtonHandler() {
  const action = resolveAndroidBackAction({
    isPlayDistribution: isAndroidPlayStoreDistribution(),
    pinOpen: isKioskServicePromptOpen(),
    wifiSheetOpen: isWifiSetupSheetOpen(),
    statusOpen: unref(showDebugModal)
  })
  switch (action) {
    case 'cancel_pin':
      cancelKioskServicePrompt()
      break
    case 'close_wifi':
      consumeWifiSetupBack()
      break
    case 'close_status':
      setShowDebugModal(false)
      break
    case 'exit_app':
      void getPlatformInstance().app.exitApp()
      break
    case 'noop':
      break
  }
}

function scheduleMouseMovingOff() {
  if (mouseInterval !== null) {
    clearInterval(mouseInterval)
    mouseInterval = null
  }
  mouseInterval = setInterval(() => {
    mouseMoving.value = false
    if (mouseInterval !== null) {
      clearInterval(mouseInterval)
      mouseInterval = null
    }
  }, 1000)
}

function mouseMoveEvtHandler() {
  mouseMoving.value = true
  scheduleMouseMovingOff()
}

function fullScreenEventHandler() {
  isFullScreen.value = document.fullscreenElement !== null
}

function setEventListeners() {
  if (getPlatform() === 'android') {
    document.addEventListener('backbutton', backButtonHandler)
  }

  window.addEventListener('click', statusOverlayTripleTapClickHandler, true)
  window.addEventListener('keydown', statusOverlayTripleTapKeyHandler, true)
  window.addEventListener('mousemove', mouseMoveEvtHandler)
  window.addEventListener('fullscreenchange', fullScreenEventHandler)
  // required to prevent 'confirm navigation' dialog on android if the user interacts with the screen
  // PS: also required in window.addEventListener('beforeunload'...
  // https://stackoverflow.com/questions/45710504/how-to-remove-confirm-navigation-pop-up-in-mobile-app
  window.onbeforeunload = null
}

function removeEventListeners() {
  if (getPlatform() === 'android') {
    document.removeEventListener('backbutton', backButtonHandler)
  }
  window.removeEventListener('click', statusOverlayTripleTapClickHandler, true)
  window.removeEventListener('keydown', statusOverlayTripleTapKeyHandler, true)
  window.removeEventListener('mousemove', mouseMoveEvtHandler)
  window.removeEventListener('fullscreenchange', fullScreenEventHandler)
  statusOverlayGesture.reset()
  if (mouseInterval !== null) {
    clearInterval(mouseInterval)
    mouseInterval = null
  }
}

function useEventListeners() {
  return {
    setEventListeners,
    removeEventListeners
  }
}

export default useEventListeners
